<template>
	<sg-select-modal
		v-if="myUser?.internalUser"
		ref="selectModal"
		v-model:single="customerValue"
		:items="customerSelectItems"
		:unselectable="false"
		:modal-title="$t('customer-select.title')"
	>
		<slot></slot>
	</sg-select-modal>
</template>

<script setup lang="ts">
	import {
		useCustomers,
		useMyCustomer,
	} from "seedgreen-shared/queries/customer";
	import SgSelectModal from "@/components/ui/modals/SgSelectModal.vue";
	import { loadingController, useIonRouter } from "@ionic/vue";
	import { computed, ref, watch } from "vue";
	import { useI18n } from "vue-i18n";
	import { useMyUser, useSetCustomer } from "seedgreen-shared/queries/user";
	import { sendToast } from "@/utils/sendToast";
	import _ from "lodash";
	import { useLotSearchStore } from "@/views/LotSearch/LotSearchStore";
	import { useBerriesHarvestStore } from "@/views/Harvest/Berries/HarvestBerriesStore";

	const { t } = useI18n();
	const { myUser } = useMyUser();
	const { myCustomer } = useMyCustomer();
	const { customers } = useCustomers();
	const { setCustomer } = useSetCustomer();

	// https://github.com/vuejs/core/issues/8373
	const selectModal = ref<{ openModal: () => void }>();

	function open() {
		selectModal.value?.openModal();
	}

	defineExpose({ open });

	watch(myCustomer, (newCustomer) => {
		customerValue.value = newCustomer?.id;
	});

	const customerValue = ref<number | undefined>(myCustomer.value?.id);
	watch(customerValue, (newCustomerId) => {
		if (newCustomerId && newCustomerId !== myCustomer.value?.id) {
			changeCustomer(newCustomerId);
		}
	});

	const customerSelectItems = computed(() =>
		_.sortBy(
			customers.value?.map((customer) => ({
				id: customer.id,
				text: customer.name,
			})) ?? [],
			(item) => item.text,
		),
	);

	const router = useIonRouter();

	async function changeCustomer(customerId: number) {
		const loading = await loadingController.create({
			message: t("customer-select.changing-customer"),
		});
		await loading.present();
		router.replace("/");

		try {
			await setCustomer(customerId);
			useLotSearchStore().$reset();
			useBerriesHarvestStore().resetFilters();
			sendToast(t("customer-select.change-success"), 2000, "success");
		} catch {
			sendToast(t("customer-select.change-failed"), 2000, "danger");
		} finally {
			await loading.dismiss();
		}
	}
</script>
